/**
 * Integration Configuration Steps (Continued)
 * Field Mapping, Transformation, Validation, Schedule, Error Handling, Review
 */

import React, { useState } from 'react';
import { Plus, Trash2, ArrowRight, Check, X, TestTube, HelpCircle } from 'lucide-react';
import { IntegrationConfig } from '@/services/api/integrations';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface StepProps {
  formData: Partial<IntegrationConfig>;
  updateFormData: (updates: Partial<IntegrationConfig>) => void;
}

// Data Sync Options for HRIS/Benefit Systems
const DATA_SYNC_OPTIONS = [
  // Core Employee Data
  { id: 'employees', label: 'Employee Demographics', description: 'Name, address, contact info, employment status', category: 'core' },
  { id: 'dependents', label: 'Dependents', description: 'Dependent information and coverage eligibility', category: 'core' },

  // Benefit Elections & Plans
  { id: 'benefit_elections', label: 'Benefit Elections', description: 'Medical, dental, vision plan selections and coverage levels', category: 'elections' },
  { id: 'enrollments', label: 'Enrollment Events', description: 'Open enrollment, qualifying life events, new hire enrollments', category: 'elections' },
  { id: 'plans', label: 'Plan Details & Comparison', description: 'Plan names, costs, coverage tiers, network details', category: 'elections' },
  { id: 'deductions', label: 'Payroll Deductions', description: 'Employee and employer contribution amounts', category: 'elections' },

  // Health Savings & Spending Accounts
  { id: 'hsa', label: 'Health Savings Account (HSA)', description: 'HSA balances, contributions, investment options', category: 'accounts' },
  { id: 'hsa_transactions', label: 'HSA Transactions & Usage', description: 'HSA claims, payments, eligible expenses tracking', category: 'accounts' },
  { id: 'fsa', label: 'Flexible Spending Account (FSA)', description: 'FSA balances, contributions, grace period status', category: 'accounts' },
  { id: 'fsa_transactions', label: 'FSA Transactions & Usage', description: 'FSA claims, reimbursements, eligible expenses', category: 'accounts' },
  { id: 'hra', label: 'Health Reimbursement Arrangement (HRA)', description: 'HRA balances and employer contributions', category: 'accounts' },
  { id: 'dcfsa', label: 'Dependent Care FSA', description: 'Dependent care FSA balances and usage', category: 'accounts' },

  // Pharmacy & Formulary
  { id: 'formulary', label: 'Drug Formulary', description: 'Covered medications, tiers, prior auth requirements', category: 'pharmacy' },
  { id: 'pharmacy_benefits', label: 'Pharmacy Benefits', description: 'Copays, coinsurance, specialty drug coverage', category: 'pharmacy' },
  { id: 'pharmacy_claims', label: 'Pharmacy Claims History', description: 'Prescription fills, costs, pharmacy locations', category: 'pharmacy' },

  // Additional Benefits
  { id: 'life_insurance', label: 'Life & AD&D Insurance', description: 'Life insurance elections, beneficiaries, coverage amounts', category: 'additional' },
  { id: 'disability', label: 'Disability Insurance', description: 'Short-term and long-term disability coverage', category: 'additional' },
  { id: 'retirement', label: '401(k) / Retirement Plans', description: 'Retirement plan contributions and vesting', category: 'additional' },
];

const DATA_SYNC_CATEGORIES = [
  { id: 'core', label: 'Core Employee Data', icon: '👤' },
  { id: 'elections', label: 'Benefit Elections & Plans', icon: '📋' },
  { id: 'accounts', label: 'Health & Spending Accounts', icon: '💰' },
  { id: 'pharmacy', label: 'Pharmacy & Formulary', icon: '💊' },
  { id: 'additional', label: 'Additional Benefits', icon: '🛡️' },
];

// Data Sync Types Step (for HRIS/Benefit Systems)
export const DataSyncTypesStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const { t } = useAppTranslation();
  const syncOptions = formData.sync_options || [];

  const toggleOption = (optionId: string) => {
    const current = syncOptions as string[];
    if (current.includes(optionId)) {
      updateFormData({ sync_options: current.filter(id => id !== optionId) });
    } else {
      updateFormData({ sync_options: [...current, optionId] });
    }
  };

  const selectAllInCategory = (categoryId: string) => {
    const categoryOptions = DATA_SYNC_OPTIONS.filter(opt => opt.category === categoryId);
    const current = syncOptions as string[];
    const allSelected = categoryOptions.every(opt => current.includes(opt.id));

    if (allSelected) {
      // Deselect all in category
      updateFormData({ sync_options: current.filter(id => !categoryOptions.some(opt => opt.id === id)) });
    } else {
      // Select all in category
      const newOptions = [...new Set([...current, ...categoryOptions.map(opt => opt.id)])];
      updateFormData({ sync_options: newOptions });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {t('integrations.selectDataTypes', 'Select Data Types to Sync')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t('integrations.selectDataTypesDescription', 'Choose which data types to synchronize from your HRIS/Benefit system. You can select multiple options across categories.')}
        </p>
      </div>

      <div className="space-y-4 max-h-[450px] overflow-y-auto pr-2">
        {DATA_SYNC_CATEGORIES.map(category => {
          const categoryOptions = DATA_SYNC_OPTIONS.filter(opt => opt.category === category.id);
          const selectedCount = categoryOptions.filter(opt => (syncOptions as string[]).includes(opt.id)).length;
          const allSelected = selectedCount === categoryOptions.length;

          return (
            <div key={category.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{category.icon}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {category.label}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedCount}/{categoryOptions.length} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => selectAllInCategory(category.id)}
                    className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                {categoryOptions.map(option => (
                  <label
                    key={option.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      (syncOptions as string[]).includes(option.id)
                        ? 'border-teal-600 bg-teal-50 dark:bg-teal-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-teal-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={(syncOptions as string[]).includes(option.id)}
                      onChange={() => toggleOption(option.id)}
                      className="mt-0.5 h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <div className="flex-1 min-w-0">
                      <h5 className="font-medium text-sm text-gray-900 dark:text-gray-100">
                        {option.label}
                      </h5>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {option.description}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <HelpCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              {t('integrations.dataSyncNote', 'Data Availability')}
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              {t('integrations.dataSyncNoteDescription', 'Not all data types may be available from every HRIS system. The actual data synced depends on what your benefit system exposes via API or file exports. Select all relevant types and the integration will sync whatever is available.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 3: Field Mapping
export const FieldMappingStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const { t } = useAppTranslation();
  const [sourceField, setSourceField] = useState('');
  const [targetField, setTargetField] = useState('');

  // Common target fields based on integration type
  const targetFieldsByType: Record<string, string[]> = {
    health_plan: [
      'medicaid_id', 'first_name', 'last_name', 'date_of_birth', 'gender',
      'street_address', 'city', 'state', 'zip_code', 'phone_number', 'email',
      'enrollment_date', 'plan_code'
    ],
    fhir: [
      'first_name', 'last_name', 'date_of_birth', 'gender', 'phone_number',
      'email', 'street_address', 'city', 'state', 'zip_code', 'mrn'
    ],
    crm: [
      'title', 'description', 'status', 'priority', 'deadline', 'assigned_to'
    ],
    housing: [
      'assessment_date', 'housing_status', 'vulnerability_score', 'homeless_duration',
      'veteran_status', 'disability_status'
    ],
    benefit_systems: [
      // Employee Demographics
      'employee_id', 'first_name', 'last_name', 'date_of_birth', 'gender',
      'email', 'phone_number', 'work_email', 'hire_date', 'termination_date',
      'department', 'job_title', 'employment_status',
      // Benefit Elections
      'plan_name', 'plan_type', 'coverage_level', 'effective_date', 'end_date',
      'employee_contribution', 'employer_contribution',
      // Dependents
      'dependent_name', 'dependent_relationship', 'dependent_dob', 'dependent_ssn',
      // HSA/FSA Accounts
      'hsa_account_number', 'hsa_balance', 'hsa_contribution_ytd', 'hsa_employer_contribution',
      'hsa_investment_balance', 'hsa_transaction_date', 'hsa_transaction_amount', 'hsa_transaction_type',
      'fsa_account_number', 'fsa_balance', 'fsa_election_amount', 'fsa_used_ytd', 'fsa_available',
      'fsa_transaction_date', 'fsa_transaction_amount', 'fsa_claim_status',
      'hra_balance', 'hra_employer_contribution', 'dcfsa_balance', 'dcfsa_election_amount',
      // Pharmacy/Formulary
      'drug_name', 'ndc_code', 'formulary_tier', 'prior_auth_required', 'quantity_limit',
      'copay_amount', 'coinsurance_percent', 'specialty_drug', 'mail_order_eligible',
      'rx_claim_date', 'rx_pharmacy_name', 'rx_days_supply', 'rx_quantity', 'rx_cost'
    ],
  };

  const availableTargets = formData.integration_type
    ? targetFieldsByType[formData.integration_type] || []
    : [];

  const mappings = formData.field_mappings || {};

  const addMapping = () => {
    if (!sourceField || !targetField) return;

    updateFormData({
      field_mappings: {
        ...mappings,
        [sourceField]: targetField
      }
    });

    setSourceField('');
    setTargetField('');
  };

  const removeMapping = (source: string) => {
    const newMappings = { ...mappings };
    delete newMappings[source];
    updateFormData({ field_mappings: newMappings });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('integrations.field_mapping', 'Field Mapping')}</h3>
        <p className="text-sm text-gray-600 mb-6">
          {t('integrations.map_external_system_fields_to', 'Map external system fields to Trust Marketplace fields')}
        </p>
      </div>

      {/* Add New Mapping */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">{t('integrations.add_field_mapping', 'Add Field Mapping')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('integrations.external_field_name', 'External Field Name')}
            </label>
            <input
              type="text"
              value={sourceField}
              onChange={(e) => setSourceField(e.target.value)}
              placeholder="subscriber_id"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('integrations.trust_marketplace_field', 'Trust Marketplace Field')}
            </label>
            <select
              value={targetField}
              onChange={(e) => setTargetField(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">{t('integrations.select_field', 'Select field...')}</option>
              {availableTargets.map(field => (
                <option key={field} value={field}>
                  {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={addMapping}
              disabled={!sourceField || !targetField}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300 transition-colors"
            >
              <Plus className="h-4 w-4" />
              {t('integrations.add_mapping', 'Add Mapping')}
            </button>
          </div>
        </div>
      </div>

      {/* Existing Mappings */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">
          Current Mappings ({Object.keys(mappings).length})
        </h4>

        {Object.keys(mappings).length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500 text-sm">{t('integrations.no_field_mappings_configured_yet', 'No field mappings configured yet')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {Object.entries(mappings).map(([source, target]) => (
              <div
                key={source}
                className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
              >
                <div className="flex-1 flex items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{source}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('integrations.external_field', 'External field')}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{target}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('integrations.internal_field', 'Internal field')}</p>
                  </div>
                </div>
                <button
                  onClick={() => removeMapping(source)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <HelpCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">{t('integrations.how_field_mapping_works_with', 'How Field Mapping Works with AI Agents')}</p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1.5">
              <li>• <strong>{t('integrations.extractor_agent', 'Extractor Agent')}</strong> reads source fields from external systems</li>
              <li>• <strong>{t('integrations.transformer_agent', 'Transformer Agent')}</strong> applies mappings and transformations</li>
              <li>• <strong>{t('integrations.validator_agent', 'Validator Agent')}</strong> ensures data quality and completeness</li>
              <li>• <strong>{t('integrations.loader_agent', 'Loader Agent')}</strong> writes data to target fields in Trust Marketplace</li>
              <li className="mt-3">💡 <em>{t('integrations.map_external_field_names_exactly', 'Map external field names exactly as they appear in the source system')}</em></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 4: Transformation Rules
export const TransformationStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const { t } = useAppTranslation();
  const [selectedField, setSelectedField] = useState('');
  const [transformType, setTransformType] = useState('');

  const transformTypes = [
    {
      value: 'uppercase',
      label: 'Uppercase',
      example: 'john → JOHN',
      description: 'Convert all text to uppercase letters'
    },
    {
      value: 'lowercase',
      label: 'Lowercase',
      example: 'JOHN → john',
      description: 'Convert all text to lowercase letters'
    },
    {
      value: 'trim',
      label: 'Trim Whitespace',
      example: ' john  → john',
      description: 'Remove leading and trailing spaces'
    },
    {
      value: 'format_phone',
      label: 'Format Phone',
      example: '1234567890 → +11234567890',
      description: 'Standardize phone numbers to E.164 format'
    },
    {
      value: 'format_date',
      label: 'Format Date',
      example: '01/15/2025 → 2025-01-15',
      description: 'Convert dates to ISO 8601 format (YYYY-MM-DD)'
    },
    {
      value: 'format_boolean',
      label: 'Boolean',
      example: 'Y/N/1/0/true/false → true/false',
      description: 'Normalize boolean values (Y, N, 1, 0, yes, no, true, false)'
    },
    {
      value: 'map',
      label: 'Map Values',
      example: 'M → Male, F → Female',
      description: 'Map specific values to other values (dropdown/single-select)'
    },
    {
      value: 'calculate',
      label: 'Calculate Field',
      example: 'field1 + field2 → calculated_value',
      description: 'Calculate value based on other fields using formula'
    },
  ];

  const rules = formData.transformation_rules || {};
  const mappedFields = Object.keys(formData.field_mappings || {});

  const addTransformation = () => {
    if (!selectedField || !transformType) return;

    updateFormData({
      transformation_rules: {
        ...rules,
        [selectedField]: {
          type: transformType,
          params: transformType === 'map' ? { mapping: {} } : {}
        }
      }
    });

    setSelectedField('');
    setTransformType('');
  };

  const removeTransformation = (field: string) => {
    const newRules = { ...rules };
    delete newRules[field];
    updateFormData({ transformation_rules: newRules });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('integrations.transformation_rules', 'Transformation Rules')}</h3>
        <p className="text-sm text-gray-600 mb-6">
          {t('integrations.apply_transformations_to_format_and', 'Apply transformations to format and normalize data during import')}
        </p>
      </div>

      {/* Add Transformation */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">{t('integrations.add_transformation', 'Add Transformation')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('integrations.field', 'Field')}
            </label>
            <select
              value={selectedField}
              onChange={(e) => setSelectedField(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">{t('integrations.select_field_1', 'Select field...')}</option>
              {mappedFields.map(field => (
                <option key={field} value={field}>{field}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('integrations.transformation_type', 'Transformation Type')}
            </label>
            <select
              value={transformType}
              onChange={(e) => setTransformType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">{t('integrations.select_type', 'Select type...')}</option>
              {transformTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {transformType && (
              <div className="mt-2 text-xs">
                <p className="text-gray-700 font-medium">
                  {transformTypes.find(tt => tt.value === transformType)?.description}
                </p>
                <p className="text-gray-500 mt-1">
                  Example: {transformTypes.find(tt => tt.value === transformType)?.example}
                </p>
              </div>
            )}
          </div>

          <div className="flex items-end">
            <button
              onClick={addTransformation}
              disabled={!selectedField || !transformType}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300 transition-colors"
            >
              <Plus className="h-4 w-4" />
              {t('integrations.add_rule', 'Add Rule')}
            </button>
          </div>
        </div>
      </div>

      {/* Existing Rules */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">
          Active Transformations ({Object.keys(rules).length})
        </h4>

        {Object.keys(rules).length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500 text-sm">No transformations configured (optional)</p>
          </div>
        ) : (
          <div className="space-y-2">
            {Object.entries(rules).map(([field, rule]: [string, any]) => (
              <div
                key={field}
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{field}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    {transformTypes.find(tt => tt.value === rule.type)?.label || rule.type}
                  </p>
                </div>
                <button
                  onClick={() => removeTransformation(field)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Step 5: Validation Rules
export const ValidationStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const { t } = useAppTranslation();
  const validationRules = formData.validation_rules || {
    required_fields: [],
    duplicate_check: false,
    data_quality_score_min: 0.8
  };

  const mappedFields = Object.values(formData.field_mappings || {});

  const toggleRequiredField = (field: string) => {
    const current = validationRules.required_fields || [];
    const updated = current.includes(field)
      ? current.filter(f => f !== field)
      : [...current, field];

    updateFormData({
      validation_rules: { ...validationRules, required_fields: updated }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('integrations.validation_rules', 'Validation Rules')}</h3>
        <p className="text-sm text-gray-600 mb-6">
          {t('integrations.configure_data_quality_and_validation', 'Configure data quality and validation requirements')}
        </p>
      </div>

      {/* Required Fields */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">{t('integrations.required_fields', 'Required Fields')}</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {mappedFields.map(field => (
            <label
              key={field}
              className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={(validationRules.required_fields || []).includes(field)}
                onChange={() => toggleRequiredField(field)}
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-900 dark:text-white">{field}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Duplicate Check */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="duplicate_check"
            checked={validationRules.duplicate_check}
            onChange={(e) => updateFormData({
              validation_rules: { ...validationRules, duplicate_check: e.target.checked }
            })}
            className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
          />
          <div className="flex-1">
            <label htmlFor="duplicate_check" className="text-sm font-medium text-gray-900 cursor-pointer">
              {t('integrations.enable_duplicate_detection', 'Enable Duplicate Detection')}
            </label>
            <p className="text-xs text-gray-600 mt-1">
              {t('integrations.check_for_duplicate_records_based', 'Check for duplicate records based on external system ID')}
            </p>

            {validationRules.duplicate_check && (
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('integrations.duplicate_check_field', 'Duplicate Check Field')}
                </label>
                <select
                  value={validationRules.duplicate_check_field || ''}
                  onChange={(e) => updateFormData({
                    validation_rules: { ...validationRules, duplicate_check_field: e.target.value }
                  })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">{t('integrations.select_field_2', 'Select field...')}</option>
                  {mappedFields.map(field => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Data Quality Score */}
      <div className="border-t border-gray-200 pt-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('integrations.minimum_data_quality_score', 'Minimum Data Quality Score')}
        </label>
        <p className="text-xs text-gray-600 mb-3">
          Reject records with quality score below this threshold (0.0 to 1.0)
        </p>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={validationRules.data_quality_score_min || 0.8}
            onChange={(e) => updateFormData({
              validation_rules: { ...validationRules, data_quality_score_min: parseFloat(e.target.value) }
            })}
            className="flex-1"
          />
          <span className="text-sm font-medium text-gray-900 w-12">
            {((validationRules.data_quality_score_min || 0.8) * 100).toFixed(0)}%
          </span>
        </div>
      </div>
    </div>
  );
};

// Step 6: Schedule Configuration
export const ScheduleStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const { t } = useAppTranslation();
  const scheduleConfig = formData.schedule_config || {
    frequency: 'daily',
    time: '02:00',
    timezone: 'America/Los_Angeles',
    enabled: false
  };

  const updateSchedule = (updates: Record<string, any>) => {
    updateFormData({
      schedule_config: { ...scheduleConfig, ...updates }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('integrations.schedule_configuration', 'Schedule Configuration')}</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure automatic execution schedule (optional)
        </p>
      </div>

      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="schedule_enabled"
          checked={scheduleConfig.enabled}
          onChange={(e) => updateSchedule({ enabled: e.target.checked })}
          className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
        />
        <div className="flex-1">
          <label htmlFor="schedule_enabled" className="text-sm font-medium text-gray-900 cursor-pointer">
            {t('integrations.enable_automatic_execution', 'Enable Automatic Execution')}
          </label>
          <p className="text-xs text-gray-600 mt-1">
            {t('integrations.run_this_integration_automatically_on', 'Run this integration automatically on a schedule')}
          </p>
        </div>
      </div>

      {scheduleConfig.enabled && (
        <div className="space-y-4 pl-7 border-l-2 border-teal-200">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('integrations.frequency', 'Frequency')}
            </label>
            <select
              value={scheduleConfig.frequency}
              onChange={(e) => updateSchedule({ frequency: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="hourly">{t('integrations.hourly', 'Hourly')}</option>
              <option value="daily">{t('integrations.daily', 'Daily')}</option>
              <option value="weekly">{t('integrations.weekly', 'Weekly')}</option>
              <option value="monthly">{t('integrations.monthly', 'Monthly')}</option>
            </select>
          </div>

          {scheduleConfig.frequency !== 'hourly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('integrations.time', 'Time')}
              </label>
              <input
                type="time"
                value={scheduleConfig.time}
                onChange={(e) => updateSchedule({ time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('integrations.timezone', 'Timezone')}
            </label>
            <select
              value={scheduleConfig.timezone}
              onChange={(e) => updateSchedule({ timezone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="America/Los_Angeles">{t('integrations.pacific_time_pt', 'Pacific Time (PT)')}</option>
              <option value="America/Denver">{t('integrations.mountain_time_mt', 'Mountain Time (MT)')}</option>
              <option value="America/Chicago">{t('integrations.central_time_ct', 'Central Time (CT)')}</option>
              <option value="America/New_York">{t('integrations.eastern_time_et', 'Eastern Time (ET)')}</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

// Step 7: Error Handling
export const ErrorHandlingStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const { t } = useAppTranslation();
  const errorConfig = formData.error_handling_config || {
    on_error: 'skip_row',
    max_retries: 3,
    retry_delay_seconds: 300,
    notify: []
  };

  const updateErrorConfig = (updates: Record<string, any>) => {
    updateFormData({
      error_handling_config: { ...errorConfig, ...updates }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('integrations.error_handling', 'Error Handling')}</h3>
        <p className="text-sm text-gray-600 mb-6">
          {t('integrations.configure_how_to_handle_errors', 'Configure how to handle errors during execution')}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('integrations.on_error_strategy', 'On Error Strategy')}
        </label>
        <div className="space-y-2">
          {[
            { value: 'skip_row', label: 'Skip Row', description: 'Skip invalid records and continue' },
            { value: 'stop_execution', label: 'Stop Execution', description: 'Stop entire execution on first error' },
            { value: 'log_and_continue', label: 'Log and Continue', description: 'Log error and continue processing' }
          ].map(strategy => (
            <label
              key={strategy.value}
              className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="radio"
                name="on_error"
                value={strategy.value}
                checked={errorConfig.on_error === strategy.value}
                onChange={(e) => updateErrorConfig({ on_error: e.target.value })}
                className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
              />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{strategy.label}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300">{strategy.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('integrations.max_retries', 'Max Retries')}
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={errorConfig.max_retries}
            onChange={(e) => updateErrorConfig({ max_retries: parseInt(e.target.value) })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Retry Delay (seconds)
          </label>
          <input
            type="number"
            min="0"
            step="60"
            value={errorConfig.retry_delay_seconds}
            onChange={(e) => updateErrorConfig({ retry_delay_seconds: parseInt(e.target.value) })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
      </div>
    </div>
  );
};

// Step 8: Review
interface ReviewStepProps extends StepProps {
  testResult: { success: boolean; message: string } | null;
  testing: boolean;
  onTest: () => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  formData,
  testResult,
  testing,
  onTest
}) => {
  const { t } = useAppTranslation();
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('integrations.review_test', 'Review & Test')}</h3>
        <p className="text-sm text-gray-600 mb-6">
          {t('integrations.review_your_configuration_and_test', 'Review your configuration and test the connection before saving')}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ReviewCard title={t('integrations.basic_info', 'Basic Info')}>
          <p><strong>{t('integrations.name', 'Name:')}</strong> {formData.name}</p>
          <p><strong>{t('integrations.type', 'Type:')}</strong> {formData.integration_type}</p>
          <p><strong>{t('integrations.adapter', 'Adapter:')}</strong> {formData.adapter_type}</p>
        </ReviewCard>

        <ReviewCard title={t('integrations.connection', 'Connection')}>
          <p><strong>{t('integrations.method', 'Method:')}</strong> {formData.connection_config?.method?.toUpperCase()}</p>
          {formData.connection_config?.host && <p><strong>{t('integrations.host', 'Host:')}</strong> {formData.connection_config.host}</p>}
          {formData.connection_config?.base_url && <p><strong>URL:</strong> {formData.connection_config.base_url}</p>}
        </ReviewCard>

        <ReviewCard title={t('integrations.field_mappings', 'Field Mappings')}>
          <p>{Object.keys(formData.field_mappings || {}).length} mappings configured</p>
        </ReviewCard>

        <ReviewCard title={t('integrations.transformations', 'Transformations')}>
          <p>{Object.keys(formData.transformation_rules || {}).length} transformations configured</p>
        </ReviewCard>

        <ReviewCard title={t('integrations.validation', 'Validation')}>
          <p>{(formData.validation_rules?.required_fields || []).length} required fields</p>
          <p>Quality threshold: {((formData.validation_rules?.data_quality_score_min || 0.8) * 100).toFixed(0)}%</p>
        </ReviewCard>

        <ReviewCard title={t('integrations.schedule', 'Schedule')}>
          <p>{formData.schedule_config?.enabled ? `${formData.schedule_config.frequency} at ${formData.schedule_config.time}` : 'Manual only'}</p>
        </ReviewCard>
      </div>

      {/* Test Connection */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">{t('integrations.connection_test', 'Connection Test')}</h4>
          <button
            onClick={onTest}
            disabled={testing}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {testing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent" />
                {t('integrations.testing', 'Testing...')}
              </>
            ) : (
              <>
                <TestTube className="h-4 w-4" />
                {t('integrations.test_connection', 'Test Connection')}
              </>
            )}
          </button>
        </div>

        {testResult && (
          <div className={`p-4 rounded-lg border ${
            testResult.success
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start gap-3">
              {testResult.success ? (
                <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <X className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <p className={`text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                {testResult.message}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Review Card Component
const ReviewCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
    <h5 className="text-sm font-medium text-gray-900 mb-2">{title}</h5>
    <div className="text-xs text-gray-600 space-y-1">
      {children}
    </div>
  </div>
);
